<template>
  <div
    class="bg-white dark:bg-n-700 rounded-lg shadow-lg border border-n-50 dark:border-n-600 flex flex-col h-full overflow-hidden"
  >
    <div class="flex-shrink-0 bg-n-100 dark:bg-n-600 p-4">
      <h3 class="font-bold text-lg text-n-900 dark:text-n-0">{{ t('summary.title') }}</h3>
    </div>

    <div class="flex-1 overflow-y-auto p-4">
      <div v-if="groupedProducts.length === 0" class="text-center text-n-500 dark:text-n-400 py-8">
        {{ t('summary.no-products') }}
      </div>
      <div v-else class="space-y-3">
        <div
          v-for="product in groupedProducts"
          :key="product.key"
          class="border-b border-n-100 dark:border-n-600 pb-2 last:border-0"
        >
          <div class="flex gap-2">
            <div class="shrink-0 w-10 text-center">
              <span class="font-bold text-xl text-n-900 dark:text-n-0">{{
                product.totalQuantity
              }}</span>
            </div>
            <div class="flex-1">
              <div class="font-medium text-base text-n-900 dark:text-n-0">
                {{ product.name }}
              </div>
              <div class="text-xs text-n-600 dark:text-n-400">
                {{ t('summary.orders-count', { count: product.orderCount }) }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

import type { KitchenOrderMapped } from '@/types'

type Props = {
  orders: KitchenOrderMapped[]
}

const props = defineProps<Props>()

const { t } = useI18n()

type GroupedProduct = {
  key: string
  name: string
  totalQuantity: number
  orderCount: number
}

const groupedProducts = computed(() => {
  const productMap = new Map<string, GroupedProduct>()

  for (const order of props.orders) {
    const activeProducts = order.mappedProducts.filter(
      p => !p.deleted && p.status !== 'completed' && p.status !== 'cancelled'
    )

    for (const product of activeProducts) {
      const modifiersKey = product.modifiers
        ?.map(m => `${m.id}-${m.quantity}`)
        .sort()
        .join('|')
      const key = `${product.name}|${product.comboName || ''}|${product.comments || ''}|${modifiersKey || ''}`

      const existing = productMap.get(key)
      if (existing) {
        existing.totalQuantity += product.quantity
        existing.orderCount += 1
      } else {
        productMap.set(key, {
          key,
          name: product.name,
          totalQuantity: product.quantity,
          orderCount: 1
        })
      }
    }
  }

  // Ordenar por cantidad descendente
  return Array.from(productMap.values()).sort((a, b) => b.totalQuantity - a.totalQuantity)
})
</script>
